/* eslint-disable no-use-before-define */
import { useEffect, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'uxd-design-system-components';
import Spinner from 'react-bootstrap/Spinner';
import { useLocation } from 'react-router-dom';
import getText from '../../../assets/locale/locale';
import LocaleContext from '../../../contexts/LocaleContext';
import './FPWelcomePage.scss';
import addDataLayerTrigger from '../../../utils/addDataLayerTrigger/addDataLayerTrigger';
import FPWelcomePageProps from '../../../types/pages/FinancialPlanning/FPWelcomePageProps';
import UserContext from '../../../contexts/UserContext';
import UserPersonaTypes from '../../../constants/UserPersonaTypes';
import { usePreviousPath } from '../../../contexts/PreviousPathContext';
import { useDataLayerInfo } from '../../../contexts/DataLayerContext';

/**
 * FPWelcomePage is the introductory screen for the quiz
 */
const FPWelcomePage = (props: FPWelcomePageProps) => {
  const { customizeBtnCallback, isLoading } = props;
  const locId = useContext(LocaleContext);
  const userPersona = useContext(UserContext);
  const [isMobile, setIsMobile] = useState(false);
  const [viewOrientation, setViewOrientation] =
    useState<OrientationType | null>(null);
  const maximumPhoneWidth = 767;

  const handleMobileView = () => {
    if (window.screen && window.screen.orientation) {
      setViewOrientation(window.screen.orientation.type);
    }
    setIsMobile(window.innerWidth < maximumPhoneWidth);
  };

  const { setPreviousPath, previousPath } = usePreviousPath();
  const currentLocation = useLocation();
  const pageTitle = getText(locId, 'pageTitle', 'fpWelcomePage');
  const pageReferrer = previousPath
    ? `${window.location.origin}${previousPath}${currentLocation.search}`
    : undefined;
  const pageURL = `${window.location.origin}${currentLocation.pathname}${currentLocation.search}`;
  const virtualPageInfo = {
    pagePath: currentLocation.pathname,
    pageTitle,
    page_referrer: pageReferrer,
    external_referrer:
      window.document.referrer && window.document.referrer.trim() !== ''
        ? window.document.referrer
        : undefined,
    page_url: pageURL,
  };
  const { dataLayerInfo } = useDataLayerInfo();
  const updatesFPPlanningTopics = {
    ...dataLayerInfo,
    page_type: 'welcome-page',
  };

  useEffect(() => {
    document.title = pageTitle;
    handleMobileView();
    window.removeEventListener('resize', handleMobileView);
    window.addEventListener('resize', handleMobileView);
  }, []);

  useEffect(() => {
    if (!isLoading) {
      setPreviousPath(currentLocation.pathname);
      addDataLayerTrigger('dataLayer-initialized', updatesFPPlanningTopics);
      addDataLayerTrigger('VirtualPageview', virtualPageInfo);
      dataLayerScroll();
    }
  }, [isLoading]);

  function dataLayerScroll() {
    let timeout: number;
    window.addEventListener(
      'scroll',
      () => {
        if (timeout) {
          window.clearTimeout(timeout);
        }
        timeout = window.setTimeout(scrollEvent, 100);
      },
      true
    );
  }

  function scrollEvent() {
    const footerLocation = document
      .querySelector('footer')
      .getBoundingClientRect();
    if (
      footerLocation.top >= 0 &&
      footerLocation.left >= 0 &&
      footerLocation.bottom <=
        (window.innerHeight || document.documentElement.clientHeight) &&
      footerLocation.right <=
        (window.innerWidth || document.documentElement.clientWidth)
    ) {
      addDataLayerTrigger('footer-visible', {});
    }
  }

  return isLoading ? (
    <div id="spinner">
      <Spinner animation="border" />
    </div>
  ) : (
    <div className="FPWelcomePage" data-testid="welcome-page-fp">
      <div className="FPWelcomePage_content">
        <h1 id="main-header" data-testid="main-header-fp">
          {getText(locId, 'mainHeader', 'fpWelcomePage')}
        </h1>
        <p className="FPWelcomePage_p" data-testid="main-header-paragraph-fp">
          {getText(locId, 'mainHeaderParagraph', 'fpWelcomePage')}
        </p>
        <div className="FPWelcomePage_cta_container">
          <h2 id="cta-container-header" data-testid="cta-container-header-fp">
            {getText(locId, 'ctaSectionHeader', 'fpWelcomePage')}
          </h2>
          {userPersona === UserPersonaTypes.DIRECT_URL ? (
            <p
              className="FPWelcomePage_p FPWelcomePage_p--secondary-one"
              data-testid="cta-container-header-paragraph-one-fp"
            >
              {getText(
                locId,
                'ctaSectionHeaderParagraphOneDefault',
                'fpWelcomePage'
              )}
            </p>
          ) : (
            <p
              className="FPWelcomePage_p FPWelcomePage_p--secondary-one"
              data-testid="cta-container-header-paragraph-one-fp"
            >
              {getText(
                locId,
                'ctaSectionHeaderParagraphOneAlternate',
                'fpWelcomePage'
              )}
            </p>
          )}
          <div className="FPWelcomePage_cta_container_cta">
            <div className="FPWelcomePage_cta_container_cta_button">
              <p
                className="FPWelcomePage_p FPWelcomePage_p--secondary-two"
                data-testid="cta-container-header-paragraph-two-fp"
              >
                {getText(
                  locId,
                  'ctaSectionHeaderParagraphTwo',
                  'fpWelcomePage'
                )}
              </p>
              <Button
                label={getText(locId, 'startBtnLabel', 'fpWelcomePage')}
                styleVariant="primary"
                onClick={() => {
                  addDataLayerTrigger('to-customize-page', {});
                  customizeBtnCallback();
                }}
                attributes={{
                  value: '',
                  id: 'customize-quiz-button-fp',
                }}
              />
            </div>
            {!isMobile ||
            (isMobile && viewOrientation === 'landscape-primary') ||
            viewOrientation === 'landscape-secondary' ? (
              <div className="image-frame" data-testid="welcome-image-frame-fp">
                <img
                  src="/public/images/welcome_image.png"
                  alt=""
                  role="presentation"
                />
              </div>
            ) : null}
          </div>
        </div>

        {isMobile &&
        (viewOrientation === 'portrait-primary' ||
          viewOrientation === 'portrait-secondary') ? (
          <div className="image-frame" data-testid="welcome-image-frame-fp">
            <img
              src="/public/images/welcome_image.png"
              alt=""
              role="presentation"
            />
          </div>
        ) : null}
        <div className="disclosureFP" data-testid="fp-disclosure">
          <div className="microcopy--2">
            {getText(locId, 'welcomePageDisclosure', 'fpWelcomePage')}
          </div>
        </div>
      </div>
    </div>
  );
};

FPWelcomePage.propTypes = {
  // eslint-disable-next-line react/no-unused-prop-types
  customizeBtnCallback: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

export default FPWelcomePage;
